import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { Routes } from "../../constants/RouteConstants";
import { type ManualQuoteDraftRecord } from "../../utils/manualQuoteDraftsStorage";
import { deleteServerDraftByQuotationId, inferWizardStepFromDraftRow, sortDraftQuoteRows } from "../../utils/quoteDraftServer";
import { getFromStorage } from "../../utils/Helper";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import { ApiTypes } from "../../constants/Constants";
import PageLoadingShell from "../layout/PageLoadingShell";
import ListPageSkeleton from "../layout/ListPageSkeleton";
import { invalidatePendingDashboardBootstrapCache } from "../../utils/pendingDashboardBootstrap";
import "./Draft.scss";
import "./DraftPage.scss";

function formatDraftDateOnly(savedAt: number): string {
  const m = moment(savedAt).clone();
  const loc = getFromStorage({ key: "Translate" }) === "spa" ? "es" : "en";
  return m.locale(loc).format("ll");
}

/** Sequelize may expose `updatedAt` / `createdAt`; API normalizes too — keep both. */
function draftRowDisplayTimestamp(row: any): string | undefined | null {
  return row?.updated_at ?? row?.updatedAt ?? row?.created_at ?? row?.createdAt;
}

function formatServerDraftDate(iso: string | undefined | null): string {
  if (!iso) return "—";
  const loc = getFromStorage({ key: "Translate" }) === "spa" ? "es" : "en";
  return moment(iso).clone().locale(loc).format("ll");
}

function stepLabel(step: number): string {
  if (step === 2) return LanguageConstants?.MANUAL_DRAFTS_STEP_2 ?? "Shipping";
  if (step === 3) return LanguageConstants?.MANUAL_DRAFTS_STEP_3 ?? "Review";
  return LanguageConstants?.MANUAL_DRAFTS_STEP_1 ?? "Items";
}

function previewCell(v: unknown, na: string): string {
  const s = String(v ?? "").trim();
  return s || na;
}

/** Per-column visible text limits (then "...") so preview fits without horizontal scroll. */
const PREVIEW_TEXT_MAX = {
  brand: 12,
  part: 10,
  desc: 10,
  serial: 8,
  model: 8,
} as const;

function previewTruncateText(v: unknown, na: string, maxLen: number): string {
  const full = previewCell(v, na);
  if (full === na || full.length <= maxLen) return full;
  return `${full.slice(0, maxLen)}...`;
}

function brandDisplayFromQuoteRow(q: any, brandNameById?: Record<string, string>): string {
  const na = LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A";
  const bidRaw = q?.brand_id ?? q?.brand_name;
  const isOther =
    String(q?.is_other_brand ?? "") === "1" ||
    String(bidRaw ?? "") === "0";
  const other = String(q?.other_brand_name ?? "").trim();
  if (isOther && other) return other;
  const rawBrandName = String(q?.brand_name ?? "").trim();
  const directBrandName =
    String(q?.brand_name_text ?? q?.brand_label ?? q?.brand_title ?? "").trim();
  const fallbackBrandName =
    rawBrandName && rawBrandName !== "0" && Number.isNaN(Number(rawBrandName))
      ? rawBrandName
      : "";
  if (directBrandName) return directBrandName;
  if (fallbackBrandName) return fallbackBrandName;
  const bdPrimary = Array.isArray(q?.brand_detail) ? q.brand_detail[0] : q?.brand_detail;
  const bdAlt = Array.isArray(q?.brand_details) ? q.brand_details[0] : q?.brand_details;
  const bd = bdPrimary ?? bdAlt;
  const nestedName =
    bd?.brands_details?.name ??
    bd?.brands_detail?.name ??
    bd?.brand_details?.name ??
    bd?.name ??
    q?.brand?.brands_details?.name;
  if (nestedName && String(nestedName).trim()) return String(nestedName).trim();
  const mapped = brandNameById?.[String(bidRaw ?? "").trim()];
  if (mapped) return mapped;
  return na;
}

/** Map API draft row to preview drawer shape (read-only). */
function serverRowToPreviewRecord(row: any, brandNameById?: Record<string, string>): ManualQuoteDraftRecord {
  const qlist = sortDraftQuoteRows(Array.isArray(row?.quotes_detail) ? row.quotes_detail : []);
  const quotes = qlist.map((q: any) => ({
    brand_name: q?.brand_id != null ? String(q.brand_id) : "",
    brand_display: brandDisplayFromQuoteRow(q, brandNameById),
    part_number: q?.part_number,
    quantity: q?.quantity,
    description: q?.description,
    serial_number: q?.serial_number ?? q?.serialNumber,
    model_number: q?.model_number ?? q?.model,
    sku: q?.sku,
  }));
  const t = draftRowDisplayTimestamp(row);
  const savedAt = t ? new Date(t).getTime() : Date.now();
  return {
    id: `acct-${row?.quotation_id}`,
    savedAt,
    quotes,
    step: inferWizardStepFromDraftRow(row),
    shippingData: {
      address_id: row?.address_id != null ? String(row.address_id) : "",
      shipping_id: row?.shipping_id != null ? String(row.shipping_id) : "",
    },
    shippingMeta: {
      residence_type: "",
      billing_same: false,
      delivery_instructions: "",
    },
    title: row?.draft_title || (row?.quotation_id != null ? `Quote ${row.quotation_id}` : ""),
  };
}

function DraftPreviewPanel({
  row,
  loading,
  onClose,
}: {
  row: ManualQuoteDraftRecord;
  loading: boolean;
  onClose: () => void;
}) {
  const quotes = Array.isArray(row.quotes) ? row.quotes : [];
  const na = LanguageConstants?.DISPLAY_VALUE_NA ?? "N/A";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside
      className={`draft-preview-panel draft-preview-panel--open${
        loading ? " draft-preview-panel--loading" : ""
      }`}
      role="region"
      aria-labelledby="draft-preview-panel-title"
      aria-busy={loading}
    >
      <div className="draft-preview-panel__inner" key={row.id}>
        <div className="draft-preview-panel__head">
          <h2 id="draft-preview-panel-title" className="draft-preview-panel__title">
            {LanguageConstants?.MANUAL_DRAFTS_COL_PREVIEW}
          </h2>
          <p className="draft-preview-panel__date">
            <i className="icon-calendar-2" aria-hidden />
            <span>{formatDraftDateOnly(row.savedAt)}</span>
          </p>
        </div>
        <div className="draft-preview-panel__body">
          <div className="draft-preview-panel__table-scroll">
            <table className="draft-preview-table table table-sm mb-0">
              <thead>
                <tr>
                  <th scope="col">{LanguageConstants?.Item}</th>
                  <th scope="col">{LanguageConstants?.BRANDNAME}</th>
                  <th scope="col">{LanguageConstants?.QTY_SHORT}</th>
                  <th scope="col">{LanguageConstants?.PARTNAME ?? LanguageConstants?.PART_NUMBER_HASH}</th>
                  <th scope="col">{LanguageConstants?.Description}</th>
                  <th scope="col">{LanguageConstants?.SERIALNUMBER}</th>
                  <th scope="col">{LanguageConstants?.MODELNUMBER}</th>
                </tr>
              </thead>
              <tbody className={loading ? "" : "draft-preview-table__tbody--ready"}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="draft-preview-table__loading-cell">
                      <span
                        className="draft-preview-table__spinner"
                        role="status"
                        aria-label="Loading"
                      />
                    </td>
                  </tr>
                ) : quotes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="draft-preview-table__empty">
                      {LanguageConstants?.DATANOTFOUND}
                    </td>
                  </tr>
                ) : (
                  quotes.map((q: any, i: number) => {
                    const brand = q?.brand_display ?? previewCell(q?.brand_name, na);
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="draft-preview-table__cell--clip" title={brand}>
                          {previewTruncateText(brand, na, PREVIEW_TEXT_MAX.brand)}
                        </td>
                        <td className="draft-preview-table__cell--qty">{previewCell(q?.quantity, na)}</td>
                        <td
                          className="draft-preview-table__cell--clip"
                          title={previewCell(q?.part_number, na)}
                        >
                          {previewTruncateText(q?.part_number, na, PREVIEW_TEXT_MAX.part)}
                        </td>
                        <td
                          className="draft-preview-table__cell--clip"
                          title={previewCell(q?.description, na)}
                        >
                          {previewTruncateText(q?.description, na, PREVIEW_TEXT_MAX.desc)}
                        </td>
                        <td
                          className="draft-preview-table__cell--clip"
                          title={previewCell(q?.serial_number, na)}
                        >
                          {previewTruncateText(q?.serial_number, na, PREVIEW_TEXT_MAX.serial)}
                        </td>
                        <td
                          className="draft-preview-table__cell--clip"
                          title={previewCell(q?.model_number, na)}
                        >
                          {previewTruncateText(q?.model_number, na, PREVIEW_TEXT_MAX.model)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </aside>
  );
}

const Draft = () => {
  const navigate = useNavigate();
  const [previewRow, setPreviewRow] = useState<ManualQuoteDraftRecord | null>(null);
  const [previewQuotationId, setPreviewQuotationId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const closePreview = useCallback(() => {
    setPreviewLoading(false);
    setPreviewRow(null);
    setPreviewQuotationId(null);
  }, []);
  const accountDraftList = useMakeRequest();
  const brandList = useMakeRequest();
  const [accountDraftsLoaded, setAccountDraftsLoaded] = useState(false);

  const brandNameById = useMemo(() => {
    const rows = Array.isArray(brandList?.data?.data) ? brandList.data.data : [];
    return rows.reduce((acc: Record<string, string>, item: any) => {
      const id = String(item?.id ?? "").trim();
      const name = String(item?.brands_details?.name ?? item?.name ?? "").trim();
      if (id && name) acc[id] = name;
      return acc;
    }, {});
  }, [brandList?.data?.data]);

  const openPreview = useCallback(
    (row: any) => {
      const qid = row?.quotation_id != null ? String(row.quotation_id) : null;
      setPreviewQuotationId(qid);
      setPreviewRow(serverRowToPreviewRecord(row, brandNameById));
      setPreviewLoading(true);
      requestAnimationFrame(() => {
        setPreviewLoading(false);
      });
    },
    [brandNameById],
  );

  const reloadAccountDrafts = useCallback(() => {
    void accountDraftList.makeRequest({
      url: ApiConstants?.QuotesDraftList,
      payload: {
        search: "",
        page: 0,
        pageSize: 100,
        orderDirection: "DESC",
      },
      type: ApiTypes?.POST,
      toastStatus: false,
    });
  }, [accountDraftList]);

  useEffect(() => {
    void brandList.makeRequest({ url: ApiConstants?.BrandList, toastStatus: false });
  }, []);

  useEffect(() => {
    let alive = true;
    void accountDraftList
      .makeRequest({
        url: ApiConstants?.QuotesDraftList,
        payload: {
          search: "",
          page: 0,
          pageSize: 100,
          orderDirection: "DESC",
        },
        type: ApiTypes?.POST,
        toastStatus: false,
      })
      .finally(() => {
        if (!alive) return;
        setAccountDraftsLoaded(true);
        invalidatePendingDashboardBootstrapCache();
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleResumeDraft = (quotationId: string | number) => {
    navigate(`${Routes.RequestQuoteCreate}?draft=${encodeURIComponent(String(quotationId))}`);
  };

  const handleDeleteServerDraft = (row: any) => {
    const qid = row?.quotation_id;
    Swal.fire({
      title: String(LanguageConstants?.MANUAL_DRAFTS_DELETE_CONFIRM_TITLE ?? "Delete Draft").replace(/\?\s*$/, ""),
      html: `${
        (LanguageConstants as any)?.MANUAL_DRAFTS_DELETE_CONFIRM_HTML ??
        `${LanguageConstants?.MANUAL_DRAFTS_DELETE_CONFIRM_TEXT}<br/><br/>${
          (LanguageConstants as any)?.MANUAL_DRAFTS_DELETE_CONFIRM_CONTINUE ?? "Do you want to continue?"
        }`
      }`,
      showCancelButton: true,
      buttonsStyling: false,
      reverseButtons: true,
      focusCancel: true,
      cancelButtonText: LanguageConstants?.NO,
      confirmButtonText: LanguageConstants?.YES,
      customClass: {
        popup: "draft-delete-confirm",
        title: "draft-delete-confirm__title",
        htmlContainer: "draft-delete-confirm__copy",
        actions: "draft-delete-confirm__actions",
        cancelButton: "draft-delete-confirm__btn draft-delete-confirm__btn--no",
        confirmButton: "draft-delete-confirm__btn draft-delete-confirm__btn--yes",
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      const ok = await deleteServerDraftByQuotationId(qid != null ? String(qid) : "");
      if (ok) {
        toast.success(LanguageConstants?.MANUAL_DRAFT_DELETED);
        invalidatePendingDashboardBootstrapCache();
        if (previewQuotationId != null && String(qid) === String(previewQuotationId)) {
          closePreview();
        }
        reloadAccountDrafts();
      }
    });
  };

  const accountDraftRows: any[] = Array.isArray(accountDraftList?.data?.data?.rows)
    ? accountDraftList.data.data.rows
    : [];

  const showInitialPageLoader =
    !accountDraftsLoaded &&
    (accountDraftList.loading || (!accountDraftList.status && accountDraftList.error == null));
  const showFullEmpty = accountDraftsLoaded && !accountDraftList.loading && accountDraftRows.length === 0;

  return (
    <PageLoadingShell
      loading={showInitialPageLoader}
      skeleton={<ListPageSkeleton />}
      className="head-space"
    >
      <div className="inner-content">
        <div className="container">
          <section className={`draft-page${previewRow ? " draft-page--split" : ""}`}>
            {showFullEmpty ? (
              <div className="draft-page__empty">
                <p className="draft-page__empty-text mb-0">{LanguageConstants?.MANUAL_DRAFTS_EMPTY}</p>
              </div>
            ) : (
              <div className="draft-page__split-layout">
                {accountDraftRows.length > 0 ? (
                  <div className="draft-page__list">
                    <div
                      className={`draft-table-scroll table-responsive${
                        accountDraftRows.length > 10 ? " draft-table-scroll--max-10" : ""
                      }`}
                    >
                      <table className="draft-table table align-middle mb-0">
                        <thead>
                          <tr>
                            <th>{LanguageConstants?.MANUAL_DRAFTS_COL_PREVIEW}</th>
                            <th>{LanguageConstants?.MANUAL_DRAFTS_COL_SAVED}</th>
                            <th>{LanguageConstants?.MANUAL_DRAFTS_COL_ITEMS}</th>
                            <th>{LanguageConstants?.ACTION}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountDraftRows.map((row: any) => {
                            const qid = row?.quotation_id;
                            const preview = String(row?.draft_title || row?.quotation_id || "").trim();
                            const itemCount = Number(row?.items_count ?? 0) || (Array.isArray(row?.quotes_detail) ? row.quotes_detail.length : 0);
                            const previewAria = preview
                              ? `${LanguageConstants?.MANUAL_DRAFTS_COL_PREVIEW ?? "Draft preview"}: ${preview}`
                              : (LanguageConstants?.MANUAL_DRAFTS_COL_PREVIEW ?? "Draft preview");
                            const updated = draftRowDisplayTimestamp(row);
                            const isPreviewActive =
                              previewQuotationId != null && String(qid) === previewQuotationId;
                            return (
                              <tr
                                key={`acct-${qid}-${row?.draft_id ?? row?.id}`}
                                className={isPreviewActive ? "is-preview-active" : undefined}
                              >
                                <td>
                                  <div className="draft-preview-cell draft-preview-cell--icon-only">
                                    <button
                                      type="button"
                                      className={`draft-icon-btn${isPreviewActive ? " is-active" : ""}`}
                                      aria-label={previewAria}
                                      aria-pressed={isPreviewActive}
                                      onClick={() =>
                                        isPreviewActive ? closePreview() : openPreview(row)
                                      }
                                    >
                                      <span className="draft-icon">
                                        <i className="icon-file" />
                                      </span>
                                    </button>
                                  </div>
                                </td>
                                <td>{formatServerDraftDate(updated)}</td>
                                <td>{itemCount}</td>
                                <td>
                                  <div className="draft-actions">
                                    <button
                                      type="button"
                                      className="draft-action draft-action--edit"
                                      aria-label={LanguageConstants?.EDIT}
                                      onClick={() => handleResumeDraft(qid)}
                                    >
                                      <i className="icon-edit" />
                                    </button>
                                    <button
                                      type="button"
                                      className="draft-action draft-action--delete"
                                      aria-label={LanguageConstants?.REMOVE}
                                      onClick={() => handleDeleteServerDraft(row)}
                                    >
                                      <i className="icon-delete-2" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
                {previewRow ? (
                  <DraftPreviewPanel
                    row={previewRow}
                    loading={previewLoading}
                    onClose={closePreview}
                  />
                ) : null}
              </div>
            )}
          </section>
        </div>
      </div>
    </PageLoadingShell>
  );
};

export default Draft;
